import React, { Component } from 'react';
import { Glyphicon, FormGroup, FormControl, Table } from 'react-bootstrap';
import LoaderButton from '../../components/LoaderButton';
import * as constants from '../../constants/MassScheduling';
import { validateFile, validateline } from './ValidateCSV';
import {
  CreateScheduleChange,
  GetAvailableFridgeSchemas,
  GetPlanogramNames,
  GetPriceMapTemplates,
} from '../../libs/GraphQL';
import './MassSchedulingChangeGraphQL.css';

// TODO: Validate csv for extra spaces
/* TODO: rewrite functions to prevent ESlint errors, mostly issues with variables */
/* eslint-disable react/no-unused-state, vars-on-top, no-var, block-scoped-var, no-redeclare. no-shadow, no-continue */

export default class ScheduleChange extends Component {
  constructor(props) {
    super(props);
    this.file = null;
    this.state = {
      isLoading: false,
      isInitialLoading: true, // Track initial data loading
      file: '',
      fileContents: '',
      successInfo: null, // { processed: number, total: number }
      apiErrors: null, // { errors: [{location, attribute, message}], failedCount: number }
    };
    this.makeChanges = this.makeChanges.bind(this);
    this.formatTimestamp = this.formatTimestamp.bind(this);
    this.scheduleHolidayScreens = this.scheduleHolidayScreens.bind(this);
    this.scheduleAllocationReason = this.scheduleAllocationReason.bind(this);
  }

  async componentDidMount() {
    try {
      // Load all initial data before allowing file uploads
      const promises = [];

      // Load planogram names
      promises.push(
        GetPlanogramNames()
          .then((response) => {
            constants.ALLOWED_ATTRIBUTE_VALUES['Layout Name'] = response;
            constants.ALLOWED_ATTRIBUTE_VALUES['Layout Name'].push('');
          })
          .catch((e) => {
            console.error('Planogram names error:', e);
            constants.ALLOWED_ATTRIBUTE_VALUES['Layout Name'] = [''];
          }),
      );

      // Load price map templates
      promises.push(
        GetPriceMapTemplates()
          .then((response) => {
            constants.ALLOWED_ATTRIBUTE_VALUES['Price Map Template'] = response;
            constants.ALLOWED_ATTRIBUTE_VALUES['Price Map Template'].push('');
          })
          .catch((e) => {
            console.error('Price map templates error:', e);
            constants.ALLOWED_ATTRIBUTE_VALUES['Price Map Template'] = [''];
          }),
      );

      // Load fridge schema templates
      promises.push(
        GetAvailableFridgeSchemas()
          .then((fridgeSchemaTemplates) => {
            const parsedFridgeSchemaTemplates = fridgeSchemaTemplates?.map(
              (e) => e.schemaId,
            );
            if (
              Array.isArray(parsedFridgeSchemaTemplates) &&
              parsedFridgeSchemaTemplates.length > 0
            ) {
              constants.ALLOWED_ATTRIBUTE_VALUES['Fridge Schema Template'] = [
                ...parsedFridgeSchemaTemplates,
                '',
              ];
            } else {
              console.error(
                'Fridge schema templates are not available for validation.',
              );
              constants.ALLOWED_ATTRIBUTE_VALUES['Fridge Schema Template'] = [
                '',
              ];
            }
          })
          .catch((e) => {
            console.error('Fridge schema error:', e);
            constants.ALLOWED_ATTRIBUTE_VALUES['Fridge Schema Template'] = [''];
          }),
      );

      // Wait for all initial data to load
      await Promise.all(promises);

      // Enable file uploads once all data is loaded
      this.setState({ isInitialLoading: false });
    } catch (e) {
      console.error('Initialization error:', e);
      // Even if there's an error, allow file uploads to prevent blocking the UI
      this.setState({ isInitialLoading: false });
    }
  }

  async makeChanges(event) {
    this.setState({
      isLoading: true,
      successInfo: null, // Reset success state
      apiErrors: null, // Reset API errors
    });
    const localFileContents = this.state.fileContents.data;
    const remainingItems = [];
    const processedLocations = new Set(); // Track unique locations processed
    const failedLocations = new Set(); // Track locations that failed
    const apiErrors = []; // Collect API errors
    const totalValidRows = localFileContents.filter((obj) => {
      return obj._validation && obj._validation.status === 0;
    }).length;

    for (let i = 0; i < localFileContents.length; i++) {
      const obj = localFileContents[i];
      // Use cached validation results instead of re-validating
      if (obj._validation && obj._validation.status === 0) {
        const location = obj.location;

        try {
          // eslint-disable-next-line no-prototype-builtins
          if (constants.HOLIDAY_KEYS.every((key) => obj.hasOwnProperty(key))) {
            // object contains both holiday start and end dates for scheduling
            await this.scheduleHolidayScreens(obj);
          }
          // eslint-disable-next-line no-prototype-builtins
          if (obj.hasOwnProperty('Ready for Allocation Update Reason')) {
            // update reason for scheduling
            await this.scheduleAllocationReason(obj);
          }

          const keys = Object.keys(obj);
          // eslint-disable-next-line no-shadow
          for (let i = 0; i < keys.length; i++) {
            if (!constants.ATTR_WITH_TS.includes(keys[i])) {
              // no action needed
              continue;
            } else {
              const attribute = keys[i];
              let value = obj[keys[i]];
              if (value === '') {
                // empty cell in the csv
                continue;
              }
              if (attribute === 'Fridge Schema Template') {
                // append /latest to schema name
                value += '/latest';
                // logging what the attribute will be in the DB
              }
              const timestamp = new Date(obj[`${keys[i]} Timestamp`]);
              const response = await CreateScheduleChange([
                {
                  location,
                  attribute: constants.ALLOWED_ATTRIBUTES[attribute],
                  value,
                  dateTime: this.formatTimestamp(
                    timestamp.setMinutes(
                      timestamp.getMinutes() + timestamp.getTimezoneOffset(),
                    ),
                  ),
                },
              ]);
            }
          }

          // Only mark as processed if no errors occurred
          processedLocations.add(location);
        } catch (error) {
          // Capture the error details
          failedLocations.add(location);

          // Extract meaningful error message
          // GraphQL errors come as an errors[] array; regular JS errors have .message directly
          const errorMessage =
            error.errors?.[0]?.message ||
            error.message ||
            'Unknown error occurred';

          apiErrors.push({
            location,
            message: errorMessage,
          });

          // Keep this item in the list so user can fix and retry
          remainingItems.push(obj);
        }
      } else if (obj._validation && obj._validation.status < 0) {
        remainingItems.push(obj);
      }
    }

    const fileContents = {
      ...this.state.fileContents,
      data: remainingItems,
    };

    // Set success info if any locations were processed
    const successInfo =
      processedLocations.size > 0
        ? {
            processed: processedLocations.size,
            total: totalValidRows,
          }
        : null;

    // Set API error info if any locations failed
    const apiErrorInfo =
      apiErrors.length > 0
        ? {
            errors: apiErrors,
            failedCount: failedLocations.size,
          }
        : null;

    this.setState({
      fileContents,
      isLoading: false,
      successInfo,
      apiErrors: apiErrorInfo,
    });
  }

  handleFileChange = (event) => {
    this.setState({
      fileContents: '',
      successInfo: null, // Reset success state when new file is uploaded
      apiErrors: null, // Reset API errors when new file is uploaded
    });
    window.Papa.parse(event.target.files[0], {
      header: true,
      dynamicTyping: true,
      complete: function (results) {
        results.data = results.data
          .map((item) => {
            item.location = item.location.toString();
            return item;
          })
          .filter((obj) => {
            const validation = validateline(obj, this.props);
            obj._validation = validation; // Store validation results
            return validation.status <= 0;
          });

        this.setState({
          fileContents: results,
        });
      }.bind(this),
    });
    this.setState({
      // eslint-disable-next-line react/no-unused-state
      file: event.target.files[0],
    });
  };

  formatTimestamp(d) {
    d = new Date(d);
    return `${d.getFullYear()}-${`0${d.getMonth() + 1}`.slice(
      -2,
    )}-${`0${d.getDate()}`.slice(-2)}T${`0${d.getHours()}`.slice(
      -2,
    )}:${`0${d.getMinutes()}`.slice(-2)}:${`0${d.getSeconds()}`.slice(
      -2,
    )}.000Z`;
  }

  formatErrorMessage(message) {
    // Regex to match ISO 8601 timestamps (e.g., 2026-02-12T17:15:00+00:00)
    const isoTimestampRegex =
      /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:\d{2}|Z)/g;

    return message.replace(isoTimestampRegex, (match) => {
      const date = new Date(match);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    });
  }

  async scheduleHolidayScreens(obj) {
    if (
      obj['Holiday Screen Start Date'] === '' ||
      obj['Holiday Screen End Date'] === ''
    ) {
      // keys present but no value
      // no action needed
      return;
    }
    let timestamp = new Date();
    let response = {};

    const holidayValues = {
      'Holiday Screen Start Date': obj['Holiday Screen Start Date'],
      'Holiday Screen End Date': obj['Holiday Screen End Date'],
      'Holiday Screen Enabled': true,
    };

    const keys = Object.keys(holidayValues);
    for (let i = 0; i < keys.length; i++) {
      // date should be reset every time since we are converting it to UTC while invoking schedule API
      // values will be scheduled to update 15 mins from now, to avoid any erroneous cases where the schedule lambda misses finding this update
      // this also will give user enough time to see if the schedule is set correctly on CMS
      timestamp = new Date();
      response = await CreateScheduleChange([
        {
          location: obj.location,
          attribute: constants.ALLOWED_ATTRIBUTES[keys[i]],
          value: holidayValues[keys[i]],
          dateTime: this.formatTimestamp(
            timestamp.setMinutes(
              timestamp.getMinutes() + 15 + timestamp.getTimezoneOffset(),
            ),
          ),
        },
      ]);
    }
  }

  async scheduleAllocationReason(obj) {
    let reason = obj['Ready for Allocation Update Reason'];

    if (reason === '') {
      // key present but no value
      // no action needed
      return;
    }

    // making reason case-insenstive so that reasons in dropdown menu don't get counted as 'Other'
    // due to case mismatch
    for (let i = 0; i < constants.ALLOCATION_UPDATE_REASONS.length; i++) {
      if (
        reason.toLowerCase() ===
        constants.ALLOCATION_UPDATE_REASONS[i].toLowerCase()
      ) {
        reason = constants.ALLOCATION_UPDATE_REASONS[i];
        break;
      }
    }

    const timestamp = new Date(obj['Ready for Allocation Timestamp']);
    let response = {};
    response = await CreateScheduleChange([
      {
        location: obj.location,
        attribute:
          constants.ALLOWED_ATTRIBUTES['Ready for Allocation Update Reason'],
        value: { prod: reason },
        dateTime: this.formatTimestamp(
          timestamp.setMinutes(
            timestamp.getMinutes() + timestamp.getTimezoneOffset(),
          ),
        ),
      },
    ]);
  }

  render() {
    let headers;
    let rows;
    let errorSummary = null;
    let successSummary = null;
    let apiErrorSummary = null;
    let validatedData = [];
    let hasValidationErrors = false;

    if (this.state.fileContents !== '') {
      headers = constants.CSV_KEYS.map((header) => (
        <th key={header}>{header}</th>
      ));
      headers.unshift(<th>Status</th>);

      // Collect and group all errors using cached validation results
      const groupedErrors = {};
      validatedData = this.state.fileContents.data
        .map((obj) => {
          // Use cached validation results - no need to re-validate
          if (obj._validation && obj._validation.status < 0) {
            hasValidationErrors = true; // Track if there are any validation errors
            obj._validation.errors.forEach((error) => {
              if (!groupedErrors[error]) {
                groupedErrors[error] = [];
              }
              groupedErrors[error].push(obj.location || 'Unknown Location');
            });
          }

          return obj;
        })
        .filter((obj) => obj._validation && obj._validation.status <= 0);

      // Create error summary if there are errors
      if (Object.keys(groupedErrors).length > 0) {
        errorSummary = (
          <div className="error-summary">
            <h5>
              <Glyphicon glyph="exclamation-sign" /> Validation Errors Found
            </h5>
            {Object.entries(groupedErrors).map(([error, locations]) => (
              <div key={error} className="error-summary-item">
                <strong>{error}</strong>
                <div className="error-summary-locations">
                  Affects locations: {[...new Set(locations)].join(', ')}
                </div>
              </div>
            ))}
          </div>
        );
      }

      rows = validatedData.map(
        function (obj) {
          const tds = constants.CSV_KEYS.map((header) => (
            <td key={header}>{obj[header]}</td>
          ));
          tds.unshift(
            <td key="status">
              {obj._validation && obj._validation.status > 0 && (
                <Glyphicon glyph="ok-circle" style={{ color: '#5cb85c' }} />
              )}
              {obj._validation && obj._validation.status === 0 && (
                <Glyphicon glyph="refresh" style={{ color: '#5bc0de' }} />
              )}
              {obj._validation && obj._validation.status < 0 && (
                <Glyphicon glyph="remove-circle" style={{ color: '#d9534f' }} />
              )}
            </td>,
          );
          return (
            <tr key={obj.location + obj.attribute + obj.timestamp}>{tds}</tr>
          );
        }.bind(this),
      );
    }

    // Create success summary if there is success info
    if (this.state.successInfo) {
      successSummary = (
        <div className="success-summary">
          <h5>
            <Glyphicon glyph="ok-sign" /> Updates Completed Successfully
          </h5>
          <div>
            <strong>
              {this.state.successInfo.processed} out of{' '}
              {this.state.successInfo.total} locations successfully updated
            </strong>
          </div>
        </div>
      );
    }

    // Create API error summary if there are API errors
    if (this.state.apiErrors) {
      // Group errors by message to avoid repetition
      const groupedApiErrors = {};
      this.state.apiErrors.errors.forEach((error) => {
        if (!groupedApiErrors[error.message]) {
          groupedApiErrors[error.message] = new Set();
        }
        groupedApiErrors[error.message].add(error.location);
      });

      apiErrorSummary = (
        <div className="api-error-summary">
          <h5>
            <Glyphicon glyph="exclamation-sign" /> API Errors Occurred
          </h5>
          <div className="api-error-summary-count">
            <strong>
              {this.state.apiErrors.failedCount} location(s) failed to update
            </strong>
          </div>
          {Object.entries(groupedApiErrors).map(([message, locations]) => (
            <div key={message} className="api-error-summary-item">
              <strong>{this.formatErrorMessage(message)}</strong>
              <div className="api-error-summary-locations">
                Affected locations: {[...locations].join(', ')}
              </div>
            </div>
          ))}
          <div className="api-error-summary-retry">
            Failed rows remain in the table below. Fix the issues and upload a
            new CSV to retry.
          </div>
        </div>
      );
    }

    return (
      <div className="Locations">
        <FormGroup controlId="file">
          <FormControl
            onClick={(event) => {
              event.target.value = null;
            }}
            onChange={this.handleFileChange}
            type="file"
            disabled={this.state.isInitialLoading}
          />
          {this.state.isInitialLoading && (
            <div className="loading-data-message">
              Loading validation data... Please wait before uploading files.
            </div>
          )}
        </FormGroup>

        <div>
          <div>
            <h4>
              Please read if you are bulk scheduling changes to Ready for
              Allocation
            </h4>
            <h5>Using the Bulk Scheduler:</h5>
            <ul>
              <li>
                Instructions on using the Bulk Update feature can be found
                through{' '}
                <a
                  target="_blank"
                  href="https://ff-eng.atlassian.net/wiki/spaces/AO/pages/193036290/How+to+bulk+schedule+updates+on+Locations+CMS+using+a+CSV"
                  rel="noreferrer"
                >
                  this link
                </a>
                . If you need access, email{' '}
                <a href="mailto:appops@farmersfridge.com">
                  appops@farmersfridge.com
                </a>
                .
              </li>
              <li>
                Template to use for bulk updates can be found{' '}
                <a
                  target="_blank"
                  href="https://docs.google.com/spreadsheets/d/1cK725CmuZIADSGXdhBjPdziADg07d06zrQNCCq9StqE/edit?usp=sharing"
                  rel="noreferrer"
                >
                  here
                </a>
                . Please make a copy, <strong>DO NOT OVERWRITE</strong>.
              </li>
            </ul>
            <h5>Making changes to Allocation?</h5>
            <ul>
              <li>
                Allocation must be <b>turned on before 6pm</b>. If not, those
                fridges will not be included in the Allocation process until the
                next time that allocation is run
              </li>
              <li>
                Fridges in the{' '}
                <b>
                  Northeast must be turned on before 6pm, 2 days prior to
                  stocking
                </b>{' '}
                or else the food will be considered expired
              </li>
              <li>
                If you cannot meet these requirements, email
                <b>AppOps@farmersfridge.com</b>
              </li>
            </ul>
            <h5>Important: Timestamp Rounding</h5>
            <ul>
              <li>
                All timestamps are automatically rounded to the{' '}
                <b>next 15-minute interval</b>
              </li>
              <li>
                For example: <code>10:55:00</code> will be scheduled for{' '}
                <code>11:00:00</code>
              </li>
              <li>
                Ensure your scheduled times account for this rounding to avoid
                unexpected timing
              </li>
            </ul>
          </div>
          &nbsp; &nbsp;
          {this.state.isLoading && (
            <div className="loading-overlay">
              <div className="loading-overlay-text">
                Processing your updates...
              </div>
              <div className="loading-spinner" />
            </div>
          )}
          {successSummary}
          {apiErrorSummary}
          {errorSummary}
          {this.state.fileContents !== '' && (
            <div className="outer-container">
              <div className="table-container">
                <Table bordered responsive>
                  <thead>
                    <tr>{headers}</tr>
                  </thead>
                  <tbody>{rows}</tbody>
                </Table>
              </div>
              <LoaderButton
                block
                bsStyle="primary"
                bsSize="large"
                disabled={
                  validateFile(this.state.fileContents) ||
                  this.state.isLoading ||
                  this.state.isInitialLoading ||
                  hasValidationErrors
                }
                onClick={this.makeChanges}
                type="submit"
                isLoading={this.state.isLoading}
                text="Schedule Changes"
                loadingText="Scheduling Changes…"
                className="schedule-button"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}
